import { useState } from 'react';
import { App, Button, Checkbox } from 'antd';

import ConfirmWordModal from '../common/ConfirmWordModal';
import { purgeHiddenValues } from '../../services/cardDefs';
import { formatCount } from '../../utils/counts';
import { inUseWords, isRetiredCard } from '../../utils/cardScopes';
import { formatDisplayDate } from '../../utils/dateDisplay';

/**
 * The card page's Danger zone tab — the collection's precedent
 * (`CollectionForm` › Danger zone) applied to a card: nothing destructive
 * hides behind a ⋯ alone, and delete arms only once the word is typed.
 *
 * The two doors that take the card's definition somewhere else (Export…,
 * History…) sit above it: they are the record-level actions that belong on
 * a settings surface rather than on the read-only home, and they open the
 * modals the form already owns.
 *
 * **Purge values of removed elements…** (delete audit F9) is the one door
 * through which values under elements the card no longer has — a removed
 * element's, a detached element set's — leave the items. Removing an
 * element only hides them. A dry run counts them first, per element (by
 * its stored name: the element is no longer on the card to give a label);
 * the purge itself is behind the typed word, can be limited to some
 * elements, and is one logged change the card's History can revert.
 * Collection cards only (a global card's items span collections; the API
 * answers 400 `purge_collection_cards_only`), and only for someone who can
 * edit the card — the API refuses an API key. A column a repeating group no
 * longer has (keepr-api KPR-751) is counted too, listed as `group › column`
 * and sent back by the name the API gave it (`group.column`).
 *
 * **Retire this card…** (keepr-api KPR-280, the owner's ruling G14:
 * "retiring replaces deleting") — an admin's door on a GLOBAL card. A
 * global card IN USE (`inUse`, the API's `usage.inUse`: a collection holds
 * it, it has items, or a card extends it) cannot be deleted, so Delete gives
 * way to Retire and the row says why in a sentence. A retired card shows
 * **Return to the Card Library** instead. Retiring is undone as easily as it
 * is done, so it asks with a plain confirmation, not the typed word. `inUse`
 * undefined (an older API, or not an admin) leaves the tab as it was: Delete,
 * and the server's refusal if it comes.
 *
 * **A published collection's card others look up** (keepr-api KPR-281, the
 * owner's ruling R2/G15) is not deleted either — the API answers 409
 * `card_in_use` — until the collection has retired. There is no Retire for a
 * collection card, so the Delete row stays and says why, without a button.
 * `lookedUpWords` is the clause the card editor builds from the API's
 * `usage.lookedUp` (`lookedUpClause`); null leaves Delete as it was.
 */
/** A purge count's line: an element by its stored name, a group's column as `group › column` (KPR-751). */
export const purgeLabel = entry => (entry?.group ? `${entry.group} › ${entry.column}` : entry?.element);

export default function CardDangerZone({
	card,
	canDelete = false,
	canExport = false,
	canViewHistory = false,
	canPurge = false,
	canRetire = false,
	inUse = undefined,
	lookedUpWords = null,
	onExport,
	onHistory,
	onDelete,
	onRetire,
	onUnretire,
}) {
	const { message, modal } = App.useApp();
	const [confirming, setConfirming] = useState(false);
	const name = (card?.name || '').trim() || 'this card';
	const cardId = card?._id ? String(card._id) : '';
	// G14: what the API said about this global card's use. `inUseWords` is
	// null when it is not in use or the API did not say.
	const retired = isRetiredCard(card);
	const usedBy = inUseWords(inUse);
	const knowsUse = Boolean(inUse && typeof inUse.inUse === 'boolean');
	const offerRetire = canRetire && !retired && knowsUse;
	const offerReturn = canRetire && retired;
	// A global card in use is retired, never deleted: the API refuses the
	// delete (409 card_in_use), so the button is not offered.
	const offerDelete = canDelete && !(usedBy && card?.scope === 'global') && !lookedUpWords;
	// G15: Delete is held, and the row says why in one sentence.
	const deleteHeld = canDelete && Boolean(lookedUpWords);
	const [retiring, setRetiring] = useState(false);

	const confirmRetire = () => {
		modal.confirm({
			title: 'Retire this card?',
			content: `${name} leaves the Card Library: no collection can add it and no new card can be built on it. Every collection that uses it keeps working, and you can return it to the library later.`,
			okText: 'Retire',
			cancelText: 'Keep it',
			onOk: async () => {
				setRetiring(true);
				try { await onRetire?.(); } finally { setRetiring(false); }
			},
		});
	};
	const returnToLibrary = async () => {
		setRetiring(true);
		try { await onUnretire?.(); } finally { setRetiring(false); }
	};

	// The purge: null until the dry run has answered with something to purge.
	const [counting, setCounting] = useState(false);
	const [purge, setPurge] = useState(null);
	const [picked, setPicked] = useState([]);
	const [purging, setPurging] = useState(false);

	const refusalText = (error, fallback) => {
		const data = error?.response?.data;
		return (data && typeof data.message === 'string' && data.message) || fallback;
	};

	const startPurge = async () => {
		if (!cardId || counting) return;
		setCounting(true);
		try {
			const counts = await purgeHiddenValues(cardId, { dryRun: true });
			if (!counts || !(counts.items > 0)) {
				message.info('Nothing to purge: no item holds a value under an element this card no longer has.');
				return;
			}
			const elements = Array.isArray(counts.elements) ? counts.elements.filter(entry => entry?.element) : [];
			setPicked(elements.map(entry => entry.element));
			setPurge({ ...counts, elements });
		} catch (error) {
			message.error(refusalText(error, 'Could not count the hidden values right now.'));
		} finally {
			setCounting(false);
		}
	};

	const runPurge = async () => {
		if (!purge || !picked.length || purging) return;
		setPurging(true);
		try {
			const every = picked.length === purge.elements.length;
			const result = await purgeHiddenValues(cardId, { dryRun: false, elements: every ? undefined : picked });
			setPurge(null);
			message.success(`Purged ${formatCount(result?.values, 'value') || 'no values'} from ${formatCount(result?.items, 'item') || 'no items'}. You can revert this from the card's History.`);
		} catch (error) {
			message.error(refusalText(error, 'The values were not purged.'));
		} finally {
			setPurging(false);
		}
	};

	return <>
		<div className="settings-column">

			{(canExport || canViewHistory) ? (
				<section className="form-panel">
					<div className="micro-label form-panel__kicker">Card definition</div>

					{canExport ? (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Export this card</span>
								<span className="toggle-row__description">
									Download the definition — its elements, options and display value — as a file.
								</span>
							</div>
							<Button onClick={onExport}>Export…</Button>
						</div>
					) : null}

					{canViewHistory ? (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">History</span>
								<span className="toggle-row__description">
									Every change made to this card, who made it, and what it was before.
								</span>
							</div>
							<Button onClick={onHistory}>History…</Button>
						</div>
					) : null}
				</section>
			) : null}

			{(offerRetire || offerReturn) ? (
				<section className="form-panel" data-testid="card-retire">
					<div className="micro-label form-panel__kicker">Card Library</div>
					{offerReturn ? (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Retired</span>
								<span className="toggle-row__description">
									{card?.retiredAt ? `Retired on ${formatDisplayDate(card.retiredAt)}. ` : null}
									It is out of the Card Library: no collection can add it and no new card can be built
									on it. Every collection that uses it keeps working.
								</span>
							</div>
							<Button loading={retiring} onClick={returnToLibrary}>Return to the Card Library</Button>
						</div>
					) : (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Retire this card</span>
								<span className="toggle-row__description">
									{usedBy
										? `${name} is in use — ${usedBy} — so it cannot be deleted. `
										: null}
									Retiring takes it out of the Card Library: no collection can add it and no new card can
									be built on it, while every collection that uses it keeps working.
								</span>
							</div>
							<Button loading={retiring} onClick={confirmRetire}>Retire card…</Button>
						</div>
					)}
				</section>
			) : null}

			{(offerDelete || deleteHeld || canPurge) ? (
				<section className="form-panel form-panel--danger">
					{canPurge ? (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Purge values of removed elements</span>
								<span className="toggle-row__description">
									Values items hold under elements this card no longer has — removed elements, or a detached
									element set's — stay on the items, hidden. Purging removes them from the items. You can
									revert it from the card's History.
								</span>
							</div>
							<Button danger loading={counting} onClick={startPurge}>Purge values of removed elements…</Button>
						</div>
					) : null}

					{deleteHeld ? (
						<div className="danger-row" data-testid="card-delete-held">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Delete this card</span>
								<span className="toggle-row__description">
									{`${name} ${lookedUpWords}, so it cannot be deleted until the collection has retired.`}
								</span>
							</div>
						</div>
					) : null}

					{offerDelete ? (
						<div className="danger-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label">Delete this card</span>
								<span className="toggle-row__description">
									Items already created from it keep their data, but no new items can use it.
									It can be restored from Recently deleted — a collection card by its managers, a global card by an administrator.
								</span>
							</div>
							<Button danger onClick={() => setConfirming(true)}>Delete card…</Button>
						</div>
					) : null}
				</section>
			) : null}

		</div>

		<ConfirmWordModal
			open={Boolean(purge)}
			word="purge"
			title="Purge values of removed elements?"
			okText="Purge values"
			extraValid={picked.length > 0 && !purging}
			onOk={runPurge}
			onCancel={() => setPurge(null)}
		>
			<p>
				{formatCount(purge?.values, 'value')} on {formatCount(purge?.items, 'item')} are stored under elements
				{(purge?.elements || []).some(entry => entry.group) ? ' or repeating group columns' : ''}
				{' '}{name} no longer has. Purging removes them from the items. The change is logged, and you can revert
				it from the card's History.
			</p>
			<div className="values-preview__checks">
				{(purge?.elements || []).map(entry => (
					<Checkbox
						key={entry.element}
						checked={picked.includes(entry.element)}
						onChange={event => setPicked(prev => (event.target.checked
							? [...prev.filter(name => name !== entry.element), entry.element]
							: prev.filter(name => name !== entry.element)))}
					>
						{`${purgeLabel(entry)} — ${formatCount(entry.items, 'item') || 'no items'}`}
					</Checkbox>
				))}
			</div>
		</ConfirmWordModal>

		<ConfirmWordModal
			open={confirming}
			word="delete"
			title="Delete this card?"
			okText="Delete"
			onOk={() => { setConfirming(false); onDelete?.(); }}
			onCancel={() => setConfirming(false)}
		>
			<p>
				Deleting <strong>{name}</strong> leaves existing items with their data,
				but no new item can use the card. You can restore it from Recently
				deleted.
			</p>
		</ConfirmWordModal>
	</>;
}
